import type { Square } from "chess.js";
import { Chess, Move } from "chess.js";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { EMOJI_SETS } from "../constants/emojiSets";

// Add timer ref outside of store to avoid React hooks
let clockTimer: NodeJS.Timeout | null = null;

export interface GameState {
  playerSet: (typeof EMOJI_SETS)[0] | null;
  opponentSet: (typeof EMOJI_SETS)[0] | null;
  unlockedSets: Set<string>;
  selectionPhase: "player" | "opponent";
  aiSkillLevel: number;
  aiElo: number;
  playAgainstAI: boolean;
  game: Chess;
  isPlayerTurn: boolean;
  playerColor: "w" | "b";
  whiteTime: number;
  blackTime: number;
  clockIsRunning: boolean;
  clockCurrentPlayer: "w" | "b";
  showWinDialog: boolean;
  showLoseDialog: boolean;
  firstMove: boolean;
  moveHistory: Move[];
  currentMoveIndex: number;
  showDrawDialog: boolean;
  isFirstCharacterScreenVisit: boolean;
  playedSets: Set<string>;
  showSetPowerDialog: boolean;
  // Custom piece tracking
  customPieces: Map<
    Square,
    {
      type: "seed" | "custom"; // Type of custom piece
      color: "w" | "b";
      powerId: string; // Which power created this piece
      metadata?: Record<string, unknown>; // Additional data for the piece
      createdTurn: number; // Turn when piece was created
    }
  >;
  setGame: (game: Chess) => void;
  getGame: () => Chess;
  setTurn: (color: "w" | "b") => void;
  switchTurn: () => void;
  setPlayerColor: (color: "w" | "b") => void;
  setIsPlayerTurn: (isTurn: boolean) => void;
  allowedMoves: ((square: Square) => string[]) | null;
  setAllowedMoves: (fn: ((square: Square) => string[]) | null) => void;
  resetAllowedMoves: () => void;
  selectedPiece: Square | null;
  setSelectedPiece: (square: Square | null) => void;
  setPlayerSet: (setId: string | null) => void;
  setOpponentSet: (setId: string | null) => void;
  setSelectionPhase: (phase: "player" | "opponent") => void;
  unlockSet: (setId: string) => void;
  isSetUnlocked: (setId: string) => boolean;
  resetGame: () => void;
  setAiSkillLevel: (level: number) => void;
  setAiElo: (elo: number) => void;
  setPlayAgainstAI: (play: boolean) => void;
  startClock: (player: "w" | "b") => void;
  pauseClock: () => void;
  resetClock: (initialTime?: number) => void;
  switchClockPlayer: () => void;
  tickClock: () => void;
  setShowWinDialog: (show: boolean) => void;
  setShowLoseDialog: (show: boolean) => void;
  setFirstMove: (first: boolean) => void;
  setMoveHistory: (history: Move[]) => void;
  navigateToMove: (index: number) => void;
  navigateBack: () => void;
  navigateForward: () => void;
  handleGameOver: (game: Chess) => void;
  handleGameEnd: () => void;
  setIsFirstCharacterScreenVisit: (isFirst: boolean) => void;
  powerState: {
    counters: Record<string, unknown>;
    usage: Record<string, { total: number; thisTurn: number }>;
  };
  premove: {
    from: Square | null;
    to: Square | null;
    promotion: string | null;
  };
  setPremove: (
    from: Square | null,
    to: Square | null,
    promotion: string | null
  ) => void;
  resetPremove: () => void;
  setPowerState: (
    partial: Partial<{
      counters: Record<string, unknown>;
      usage: Record<string, { total: number; thisTurn: number }>;
    }>
  ) => void;
  setPowerCounter: (key: string, value: unknown) => void;
  setShowDrawDialog: (show: boolean) => void;
  setShowSetPowerDialog: (show: boolean) => void;
  markSetAsPlayed: (setId: string) => void;
  isFirstTimePlayingSet: (setId: string) => boolean;
  // Custom piece methods
  addCustomPiece: (
    square: Square,
    type: "seed" | "custom",
    color: "w" | "b",
    powerId: string,
    metadata?: Record<string, unknown>
  ) => void;
  removeCustomPiece: (square: Square) => void;
  getCustomPiece: (square: Square) => {
    type: "seed" | "custom";
    color: "w" | "b";
    powerId: string;
    metadata?: Record<string, unknown>;
    createdTurn: number;
  } | null;
  isCustomPiece: (square: Square) => boolean;
  getCustomPiecesByPower: (powerId: string) => Array<{
    square: Square;
    type: "seed" | "custom";
    color: "w" | "b";
    powerId: string;
    metadata?: Record<string, unknown>;
    createdTurn: number;
  }>;
  clearCustomPieces: () => void;
}

// Initialize with first 3 sets unlocked
const INITIAL_UNLOCKED_SETS = new Set(
  EMOJI_SETS.slice(0, 3).map((set) => set.id)
);

export const useGameStore = create<GameState>()(
  devtools(
    persist(
      (setState, get) => ({
        playerSet: null,
        opponentSet: null,
        unlockedSets: INITIAL_UNLOCKED_SETS,
        selectionPhase: "player",
        aiSkillLevel: 8,
        aiElo: 1350,
        playAgainstAI: true,
        game: new Chess(),
        isPlayerTurn: true,
        playerColor: "w",
        whiteTime: 5 * 60 * 1000, // 5 minutes in milliseconds
        blackTime: 5 * 60 * 1000,
        clockIsRunning: false,
        clockCurrentPlayer: "w",
        showWinDialog: false,
        showLoseDialog: false,
        firstMove: false,
        moveHistory: [],
        currentMoveIndex: -1,
        showDrawDialog: false,
        isFirstCharacterScreenVisit: true,
        playedSets: new Set(),
        showSetPowerDialog: false,
        allowedMoves: null,
        selectedPiece: null,
        setSelectedPiece: (square: Square | null) =>
          setState({ selectedPiece: square }),
        setAllowedMoves: (fn: ((square: Square) => string[]) | null) =>
          setState({ allowedMoves: fn }),
        resetAllowedMoves: () => setState({ allowedMoves: null }),
        setGame: (game: Chess) => setState({ game }),
        getGame: () => get().game,
        setTurn: (color: "w" | "b") => {
          const currentGame = get().game;
          if (currentGame.turn() !== color) {
            // Only manipulate FEN if absolutely necessary
            const fenParts = currentGame.fen().split(" ");
            fenParts[1] = color;
            const newGame = new Chess();
            try {
              newGame.load(fenParts.join(" "));
              setState({
                game: newGame,
                isPlayerTurn: newGame.turn() === get().playerColor,
              });
            } catch (error) {
              console.error("Error setting turn:", error);
            }
          } else {
            // Just update isPlayerTurn if turn is already correct
            setState({
              isPlayerTurn: currentGame.turn() === get().playerColor,
            });
          }
        },
        switchTurn: () => {
          const state = get();
          setState({
            isPlayerTurn: !state.isPlayerTurn,
          });
        },
        setPlayerColor: (color: "w" | "b") => setState({ playerColor: color }),
        setIsPlayerTurn: (isTurn: boolean) =>
          setState({ isPlayerTurn: isTurn }),

        setPlayerSet: (setId: string | null) => {
          if (setId === null) {
            setState({ playerSet: null });
            return;
          }
          const emojiSet = EMOJI_SETS.find((s) => s.id === setId);
          if (emojiSet && get().isSetUnlocked(setId)) {
            setState({ playerSet: emojiSet });
          }
        },

        setOpponentSet: (setId: string | null) => {
          if (setId === null) {
            setState({ opponentSet: null });
            return;
          }
          const emojiSet = EMOJI_SETS.find((s) => s.id === setId);
          setState({ opponentSet: emojiSet });
        },

        setSelectionPhase: (phase: "player" | "opponent") => {
          setState({ selectionPhase: phase });
        },

        unlockSet: (setId: string) => {
          setState((state) => ({
            unlockedSets: new Set([...state.unlockedSets, setId]),
          }));
        },

        isSetUnlocked: (setId: string) => {
          return get().unlockedSets.has(setId);
        },

        resetGame: () => {
          if (clockTimer) {
            clearInterval(clockTimer);
            clockTimer = null;
          }
          setState({
            playerSet: null,
            opponentSet: null,
            unlockedSets: INITIAL_UNLOCKED_SETS,
            selectionPhase: "player",
            aiSkillLevel: 8,
            aiElo: 1350,
            playAgainstAI: true,
            game: new Chess(),
            allowedMoves: null,
            selectedPiece: null,
            isPlayerTurn: true,
            playerColor: "w",
            whiteTime: 5 * 60 * 1000,
            blackTime: 5 * 60 * 1000,
            clockIsRunning: false,
            clockCurrentPlayer: "w",
            showWinDialog: false,
            showLoseDialog: false,
            firstMove: false,
            moveHistory: [],
            currentMoveIndex: -1,
            showDrawDialog: false,
            isFirstCharacterScreenVisit: true,
            showSetPowerDialog: false,
            customPieces: new Map(),
          });
        },

        setAiSkillLevel: (level: number) => {
          setState({ aiSkillLevel: level });
        },

        setAiElo: (elo: number) => {
          setState({ aiElo: elo });
        },

        setPlayAgainstAI: (play: boolean) => {
          setState({ playAgainstAI: play });
        },

        startClock: (player: "w" | "b") => {
          // Clear any existing timer
          if (clockTimer) {
            clearInterval(clockTimer);
          }

          setState({
            clockCurrentPlayer: player,
            clockIsRunning: true,
          });

          // Start new timer
          clockTimer = setInterval(() => {
            get().tickClock();
          }, 100);
        },

        pauseClock: () => {
          if (clockTimer) {
            clearInterval(clockTimer);
            clockTimer = null;
          }
          setState({ clockIsRunning: false });
        },

        resetClock: (time = 5 * 60 * 1000) => {
          if (clockTimer) {
            clearInterval(clockTimer);
            clockTimer = null;
          }
          setState({
            whiteTime: time,
            blackTime: time,
            clockIsRunning: false,
            clockCurrentPlayer: "w",
          });
        },

        switchClockPlayer: () => {
          setState((state) => ({
            clockCurrentPlayer: state.clockCurrentPlayer === "w" ? "b" : "w",
          }));
        },

        tickClock: () => {
          const state = get();
          if (!state.clockIsRunning) return;

          setState((state) => {
            const newWhiteTime =
              state.clockCurrentPlayer === "w"
                ? Math.max(0, state.whiteTime - 100)
                : state.whiteTime;
            const newBlackTime =
              state.clockCurrentPlayer === "b"
                ? Math.max(0, state.blackTime - 100)
                : state.blackTime;

            // Handle time running out
            if (newWhiteTime === 0 || newBlackTime === 0) {
              // Clear the timer
              if (clockTimer) {
                clearInterval(clockTimer);
                clockTimer = null;
              }

              // Handle timeout scenario
              const playerColor = get().playerColor;
              const whiteTimeOut = newWhiteTime === 0;
              const blackTimeOut = newBlackTime === 0;

              // Determine if player won or lost
              const playerWon =
                (whiteTimeOut && playerColor === "b") ||
                (blackTimeOut && playerColor === "w");

              // If player won and opponent set is locked, unlock it
              if (playerWon) {
                const opponentSet = get().opponentSet;
                if (opponentSet && !get().isSetUnlocked(opponentSet.id)) {
                  get().unlockSet(opponentSet.id);
                }
              }

              // Return updated state with dialog showing
              return {
                whiteTime: newWhiteTime, // Keep actual times to show which ran out
                blackTime: newBlackTime, // Keep actual times to show which ran out
                clockIsRunning: false,
                showWinDialog: playerWon,
                showLoseDialog: !playerWon,
              };
            }

            return {
              whiteTime: newWhiteTime,
              blackTime: newBlackTime,
            };
          });
        },

        setShowWinDialog: (show: boolean) => setState({ showWinDialog: show }),
        setShowLoseDialog: (show: boolean) =>
          setState({ showLoseDialog: show }),
        setFirstMove: (first: boolean) => setState({ firstMove: first }),
        setMoveHistory: (history: Move[]) =>
          setState({
            moveHistory: history,
            currentMoveIndex: history.length - 1,
          }),

        navigateToMove: (index: number) => {
          const { moveHistory } = get();
          if (index < -1 || index >= moveHistory.length) return;

          let tempGame: Chess;

          if (index === -1) {
            // Go to starting position
            tempGame = new Chess();
          } else {
            // Use the FEN position after the specified move
            const move = moveHistory[index];
            tempGame = new Chess();
            try {
              // Use the "after" FEN from the move object if available
              if (move.after) {
                tempGame.load(move.after);
              } else {
                // Fallback: replay moves (this might still fail with powers)
                for (let i = 0; i <= index; i++) {
                  tempGame.move(moveHistory[i]);
                }
              }
            } catch (error) {
              console.error("Error loading position:", error);
              return;
            }
          }

          setState({
            currentMoveIndex: index,
            game: tempGame,
          });
        },

        navigateBack: () => {
          const { currentMoveIndex } = get();
          if (currentMoveIndex > -1) {
            get().navigateToMove(currentMoveIndex - 1);
          }
        },

        navigateForward: () => {
          const { currentMoveIndex, moveHistory } = get();
          if (currentMoveIndex < moveHistory.length - 1) {
            get().navigateToMove(currentMoveIndex + 1);
          }
        },

        setShowDrawDialog: (show: boolean) =>
          setState({ showDrawDialog: show }),

        handleGameOver: (gameCopy: Chess) => {
          if (gameCopy.isCheckmate()) {
            get().pauseClock();
            get().resetClock();
            const opponentSet = get().opponentSet;
            // If player won and opponent set is locked, unlock it
            if (gameCopy.turn() !== get().playerColor && opponentSet) {
              get().unlockSet(opponentSet.id);
              setState({ showWinDialog: true });
            } else if (gameCopy.turn() === get().playerColor) {
              // Player lost
              setState({ showLoseDialog: true });
            }
          } else if (gameCopy.isDraw()) {
            get().pauseClock();
            get().resetClock();
            setState({ showDrawDialog: true });
          }
        },

        handleGameEnd: () => {
          // Reset game state but keep player/opponent sets and unlocked sets
          const { playerSet, opponentSet, unlockedSets } = get();
          if (clockTimer) {
            clearInterval(clockTimer);
            clockTimer = null;
          }
          setState({
            game: new Chess(),
            allowedMoves: null,
            selectedPiece: null,
            isPlayerTurn: true,
            playerColor: "w",
            whiteTime: 5 * 60 * 1000,
            blackTime: 5 * 60 * 1000,
            clockIsRunning: false,
            clockCurrentPlayer: "w",
            showWinDialog: false,
            showLoseDialog: false,
            firstMove: false,
            moveHistory: [],
            currentMoveIndex: -1,
            showDrawDialog: false,
            // Keep these values
            playerSet,
            opponentSet,
            unlockedSets,
            isFirstCharacterScreenVisit: true,
            showSetPowerDialog: false,
            customPieces: new Map(),
          });
        },

        powerState: {
          counters: {} as Record<string, unknown>,
          usage: {} as Record<string, { total: number; thisTurn: number }>,
        },
        setPowerState: (partial) =>
          setState((state) => ({
            powerState: { ...state.powerState, ...partial },
          })),
        setPowerCounter: (key: string, value) =>
          setState((state) => ({
            powerState: {
              ...state.powerState,
              counters: { ...state.powerState.counters, [key]: value },
            },
          })),
        setIsFirstCharacterScreenVisit: (isFirst: boolean) =>
          setState({ isFirstCharacterScreenVisit: isFirst }),
        setShowSetPowerDialog: (show: boolean) =>
          setState({ showSetPowerDialog: show }),
        markSetAsPlayed: (setId: string) => {
          console.log("markSetAsPlayed", setId);
          setState((state) => ({
            playedSets: new Set([...state.playedSets, setId]),
          }));
        },
        isFirstTimePlayingSet: (setId: string) => !get().playedSets.has(setId),
        premove: {
          from: null,
          to: null,
          promotion: null,
        },
        setPremove: (
          from: Square | null,
          to: Square | null,
          promotion: string | null
        ) => setState({ premove: { from, to, promotion } }),
        resetPremove: () =>
          setState({ premove: { from: null, to: null, promotion: null } }),
        customPieces: new Map(),
        addCustomPiece: (
          square: Square,
          type: "seed" | "custom",
          color: "w" | "b",
          powerId: string,
          metadata?: Record<string, unknown>
        ) => {
          setState((state) => {
            const newCustomPieces = new Map(state.customPieces);
            newCustomPieces.set(square, {
              type,
              color,
              powerId,
              metadata,
              createdTurn: get().currentMoveIndex,
            });
            return { customPieces: newCustomPieces };
          });
        },
        removeCustomPiece: (square: Square) => {
          setState((state) => {
            const newCustomPieces = new Map(state.customPieces);
            newCustomPieces.delete(square);
            return { customPieces: newCustomPieces };
          });
        },
        getCustomPiece: (square: Square) => {
          return get().customPieces.get(square) || null;
        },
        isCustomPiece: (square: Square) => {
          return get().customPieces.has(square);
        },
        getCustomPiecesByPower: (powerId: string) => {
          return Array.from(get().customPieces.entries())
            .filter(([, piece]) => piece.powerId === powerId)
            .map(([square, piece]) => ({
              square,
              type: piece.type,
              color: piece.color,
              powerId: piece.powerId,
              metadata: piece.metadata,
              createdTurn: piece.createdTurn,
            }));
        },
        clearCustomPieces: () => {
          setState({ customPieces: new Map() });
        },
      }),
      {
        name: "emojichess-storage",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          unlockedSets: Array.from(state.unlockedSets),
          playerSet: state.playerSet,
          opponentSet: state.opponentSet,
          playAgainstAI: state.playAgainstAI,
          aiElo: state.aiElo,
          aiSkillLevel: state.aiSkillLevel,
          isFirstCharacterScreenVisit: state.isFirstCharacterScreenVisit,
          playedSets: Array.from(state.playedSets),
          showSetPowerDialog: state.showSetPowerDialog,
          // Exclude game state from persistence to prevent rehydration issues
          // game, moveHistory, currentMoveIndex, firstMove should not be persisted
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Convert the array back to a Set when rehydrating
            state.unlockedSets = new Set(state.unlockedSets);
            state.playedSets = new Set(state.playedSets);
          }
        },
      }
    ),
    {
      name: "EmojiChess Store",
    }
  )
);

